import { last } from "lib0/array";
import { stringify } from "lib0/json";
import { ErrorNote, LocationTrace, RuntimeError } from "../errors";
import { mapGetKey, mapUpdateKeyMutating } from "../objects/map";
import { boxApply, boxBoolean, boxNativeFunc, boxNil, boxNumber, boxRoundBlock, boxSquareBlock, Thing, ThingType, typecheck, typeNameOf } from "../objects/thing";
import { removed_whitespace } from "../patterns/meta";
import { walkEnvTree } from "../runtime/env";
import { parseSignature } from "../runtime/functor";
import type { StackEntry, Task } from "../runtime/task";
import { collections } from "./collections";
import { control_flow } from "./control_flow";
import { math } from "./math";
import { metaprogramming } from "./metaprogramming";
import { misc } from "./misc";
import { NativeModule, rewriteAsApply, symbol_x, symbol_y } from "./module";
import { strings } from "./strings";

const EXPLICIT_BLOCK_PRECEDENCE = -Infinity;
const LAMBDA_PRECEDENCE = -1e100;
export const VARIABLE_ASSIGNMENT_PRECEDENCE = 12;
const IMPLICIT_BLOCK_PRECEDENCE = 1e100;
const APPLY_PRECEDENCE = Infinity;

/**
 * @file
 * @module Builtins
 */

export function initCoreSyntax(mod: NativeModule) {
    /**
     * @backolon
     * @value nil
     * @type {nil}
     */
    mod.defvar("nil", boxNil(mod.loc));
    /**
     * @backolon
     * @value false
     * @type {number}
     */
    mod.defvar("false", boxBoolean(false, mod.loc));
    /**
     * @backolon
     * @value true
     * @type {number}
     */
    mod.defvar("true", boxBoolean(true, mod.loc));
    /**
     * @backolon
     * @value done
     * @type {done}
     */
    mod.defvar("done", new Thing(ThingType.done, [], null, "done", "", "", mod.loc));
    const xy = [symbol_x, symbol_y];
    // MARK: blocks and logical lines
    mod.defsyntax("[^] {x...|}  ;  [y{_| }...] [$]", EXPLICIT_BLOCK_PRECEDENCE, false, null, "__rewrite_sequence", (task, state) => {
        const groups: Thing<ThingType.map> = state.argv[0]! as any;
        var first = mapGetKey(groups, symbol_x);
        var second = mapGetKey(groups, symbol_y);
        if (first) {
            first = boxRoundBlock(first.c, first.loc);
            if (second && second.c.length > 0) {
                second = boxRoundBlock(second.c, second.loc);
                task.out(boxApply(boxNativeFunc("__sequence", first.loc), second ? [first, second] : [first], first.loc));
            } else {
                // effectively just strip the trailing line terminator
                task.out(first);
            }
        } else {
            // we get here if there are a sequence of consecutive newlines or semicolons.
            task.out(boxNil(groups.loc));
        }
    });
    mod.defsyntax("[^] {x...|}  (\n)  [y{_| }...] [$]", IMPLICIT_BLOCK_PRECEDENCE, false, null, "__rewrite_sequence");
    mod.defun("__sequence", "@first @rest", (task, state) => {
        const first = state.argv[0]!;
        const second = state.argv[1]!;
        task.updateCookie(1, 0);
        if (state.index === 0) {
            task.enter(boxApply(first, [], first.loc), state.loc, state.env);
        } else if (second) {
            task.out(); // tail call
            task.enter(boxApply(second, [], second.loc), state.loc, state.env);
        }
    });
    // MARK: Apply
    // First one is no arguments
    mod.defsyntax("[^] x ! [$]", APPLY_PRECEDENCE, false, null, "__rewrite_apply", (task, state) => {
        const groups: Thing<ThingType.map> = state.argv[0]! as any;
        const fun = mapGetKey(groups, symbol_x)!;
        const argv = mapGetKey(groups, symbol_y);
        const args = argv ? removed_whitespace(argv.c) : [];
        const hasArgs = args.length > 0;
        task.out(boxApply(fun, args, fun.loc, hasArgs ? "(" : "", hasArgs ? ")" : "!", true));
    });
    // Second one is with arguments
    mod.defsyntax("[^] x  y...[$]", APPLY_PRECEDENCE, false, null, "__rewrite_apply");
    // MARK: variable management
    /**
     * Declare a variable in the current scope and bind it to a value
     * @backolon
     * @category Variables & Assignment
     * @syntax Declare Variable
     * @pattern name := value
     * @example
     * ```backolon
     * x := 10
     * x := 10 # errors, x is already defined
     * ```
     */
    mod.defsyntax("x := y", VARIABLE_ASSIGNMENT_PRECEDENCE, true, null, "__rewrite_declaration", rewriteAsApply(xy, "__declare"));
    const binding_helper = (dip: boolean, cb: (state: StackEntry, name: Thing<ThingType.name>, value: Thing, loc: LocationTrace) => void, refCb: (state: StackEntry, task: Task, ref: Thing<ThingType.reference>, value: Thing, loc: LocationTrace) => void): ((task: Task, state: StackEntry) => void) => {
        return (task, state) => {
            const name = state.argv[0]!;
            const value = state.argv[1]!;
            const loc = name.loc;
            if (typecheck(ThingType.reference)(name)) {
                return refCb(state, task, name, value, loc);
            }
            if (!typecheck(ThingType.name)(name)) {
                throw new RuntimeError(`cannot assign to ${typeNameOf(name.t)}`, loc);
            }
            task.out(value);
            if (dip) task.dip(1, state => cb(state, name, value, loc));
            else cb(state, name, value, loc);
        }
    }
    mod.defun("__declare", "@name! value=nil", binding_helper(true, (state, name, value, loc) => {
        const vars = state.env;
        if (mapGetKey(vars.c[1]!, name) !== undefined) {
            throw new RuntimeError(`variable ${stringify(name.v)} already exists in this scope`, loc, [new ErrorNote(`note: change the ":=" to "=" if you just want to reassign ${stringify(name.v)}`, loc)]);
        }
        mapUpdateKeyMutating(vars.c[1]!, name, value);
        if (typecheck(ThingType.func)(value)) {
            value.v ??= name.v;
        }
    }, (_state, _task, _ref, _value, loc) => {
        throw new RuntimeError("cannot declare this", loc);
    }));
    /**
     * Assign a new value to an existing variable
     * @backolon
     * @category Variables & Assignment
     * @syntax Assign Value
     * @pattern name = value
     * @pattern lvalue = value
     * @example
     * ```backolon
     * x := 10
     * x = 20 # ok
     * y = 20 # errors, y is not defined
     * x->1 = 20 # ok if x is indexable
     * ```
     */
    mod.defsyntax("x = y", VARIABLE_ASSIGNMENT_PRECEDENCE, true, null, "__rewrite_assign", rewriteAsApply(xy, "__assign"));
    mod.defun("__assign", "@name! value", binding_helper(false, (state, name, value, loc) => {
        if (!walkEnvTree(state.env, vars => {
            if (mapGetKey(vars, name, loc) !== undefined) {
                mapUpdateKeyMutating(vars, name, value, loc);
                if (typecheck(ThingType.func)(value)) {
                    value.v ??= name.v;
                }
                return true;
            }
            return false;
        })) {
            throw new RuntimeError(`undefined: ${stringify(name.v)}`, loc, [new ErrorNote(`note: change the "=" to ":=" to declare ${stringify(name.v)} to be in this scope`, loc)]);
        };
    }, (state, task, ref, value, loc) => {
        task.out();
        task.enter(boxApply(ref.c[1], [value], loc), loc, state.env);
    }));
    // MARK: lambdas
    /**
     * Define a lambda function
     * @backolon
     * @category Lambdas & Functions
     * @syntax Lambda
     * @pattern [params] => body
     * @example
     * ```backolon
     * # simple parameters
     * [a b c] => [x] => a * x * x + b * x + c
     * # rest parameter
     * [values...] => nil
     * # lazy parameters for metaprogramming
     * or := [@first @second] => (
     *     temp = (first!)
     *     if temp (return temp)
     *     second!
     * )
     * ```
     */
    mod.defsyntax("[x:squareblock] => y...", LAMBDA_PRECEDENCE, true, null, "__rewrite_lambda", (task, state) => {
        const groups: Thing<ThingType.map> = state.argv[0]! as any;
        const name = mapGetKey(groups, symbol_x)!;
        const values = mapGetKey(groups, symbol_y)!.c as any[];
        while (typecheck(ThingType.newline, ThingType.space)(values[0])) values.shift();
        while (typecheck(ThingType.newline, ThingType.space)(last(values))) values.pop();
        const value = boxRoundBlock(values, values[0]!.loc);
        task.out(boxApply(boxNativeFunc("__build_lambda", state.value.loc), [name, value], state.value.loc));
    });
    mod.defun("__build_lambda", "@params! @body", (task, state) => {
        const params = state.argv[0]!;
        if (!typecheck(ThingType.squareblock)(params)) throw new RuntimeError(`wrong object type for lambda signature`, params.loc);
        const signature = boxSquareBlock(parseSignature(params.c), params.loc, " ");
        const body = state.argv[1]!;
        task.out(new Thing(ThingType.func, [signature, body], null, "", "", " => ", params.loc));
    });
    /**
     * Throw an error with the specified message
     * @backolon
     * @category Errors
     * @function error
     * @param {string} message
     * @returns {never}
     * @example
     * ```backolon
     * error "Something went wrong with {x}!"
     * ```
     */
    mod.defun("error", "message:string", (task, state) => {
        throw new RuntimeError(state.argv[0]!.v, state.value.loc);
    });
    // initialize everything else
    control_flow(mod);
    math(mod);
    misc(mod);
    collections(mod);
    strings(mod);
    metaprogramming(mod);
}
